/**
 * Manager-only, evidence-driven link suggestions.
 *
 * This is a conservative convenience layer over the CANONICAL training hub.
 * It cannot validate a course's accreditation, clear a role-specific practical
 * competency, restart Care Certificate achievements, charge a worker, or send a message.
 * The manager reviews and explicitly confirms which links to share.
 */

const CURRENT_OR_REVIEW = new Set([
  'verified', 'completed', 'awaiting_review', 'processing', 'needs_information', 'proposed',
]);
const RENEWAL_ACTIONS = new Set(['missing', 'expired', 'due_soon', 'rejected', 'needs_renewal']);

function requirementCode(action) {
  const raw = String(action?.id || '').startsWith('training:')
    ? String(action.id).slice('training:'.length)
    : String(action?.code || action?.training_code || '').trim();
  const aliases = {
    health_and_safety: 'health_safety',
    safeguarding_adults: 'safeguarding',
    manual_handling_people: 'manual_handling',
    moving_and_handling: 'manual_handling',
    resuscitation_adults: 'basic_life_support',
    learning_disability_autism_statutory: 'learning_disability_autism_tier_2',
  };
  // Generic statutory autism is NOT sufficient to infer Tier 2 for every role.
  if (raw === 'learning_disability_autism_statutory') return '';
  return aliases[raw] || raw;
}

export function getManagerRenewalNeeds(hub = {}) {
  const seen = new Set();
  const needs = [];
  for (const action of hub?.actions || []) {
    if (action?.source !== 'mandatory_training') continue;
    const status = String(action.status || '').toLowerCase().trim();
    if (CURRENT_OR_REVIEW.has(status)) continue;
    if (!RENEWAL_ACTIONS.has(status) && action.type !== 'arrange_training') continue;
    const code = requirementCode(action);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    needs.push({
      code,
      title: action.title || code.replace(/_/g, ' '),
      status: status || 'missing',
      dueDate: action.due_date || null,
    });
  }
  return needs;
}

export function suggestManagerRenewalLinks(hub, resources) {
  const needs = getManagerRenewalNeeds(hub);
  const suggested = [];
  const unresolved = [];
  for (const need of needs) {
    const available = (resources || []).filter(item => item.key === need.code);
    if (!available.length) {
      // Never use an unrelated/guessed URL for role-specific qualifications.
      unresolved.push(need);
      continue;
    }
    // Offer verified free learning before paid theory, but never substitute a
    // supplementary module for role-required training.
    const freeFull = available.find(item => item.price === 'Free' && !item.isSupplementary
      && item.type !== 'reference');
    const primary = freeFull
      || available.find(item => item.type === 'provider' && !item.isSupplementary)
      || available.find(item => item.type === 'practical');
    if (primary && !suggested.some(item => item.id === primary.id)) suggested.push(primary);

    // Where a course clearly specifies a practical face-to-face requirement,
    // include that route along with theory (e.g. Oliver McGowan Tier 2).
    const practical = available.find(item => item.type === 'practical');
    if (practical && !suggested.some(item => item.id === practical.id)) suggested.push(practical);
    if (need.code === 'learning_disability_autism_tier_2') {
      const partOne = available.find(item => item.type === 'free' && item.isSupplementary);
      if (partOne && !suggested.some(item => item.id === partOne.id)) suggested.push(partOne);
    }
  }
  return { needs, suggested, unresolved };
}

export const TRAINING_RENEWAL_POLICY = Object.freeze({
  internalEvidenceDays: 90,
  bridgeTitle: 'Internal training evidence',
  bridgeMessage: 'Internal course or questionnaire evidence can be accepted for up to 90 days under Osabea policy. Before that bridge ends, the manager checks which official evidence or practical assessment the role needs.',
  careCertificateTitle: 'Care Certificate achievement',
  careCertificateMessage: 'Once correctly assessed, the 16 Care Certificate standards remain achieved; they are not automatically renewed after 90 days. Separate statutory and mandatory training must stay current.',
  feeSafeguard: 'Employer-required training must not automatically be billed to staff. Check legal compliance and the employment/agency arrangement before considering any optional paid services.',
});
